import type { WorkspaceFileAttachment } from "@machina-labs/map-coordinator-sdk";
import { z } from "zod";
import {
  SKIRTED_MESH_FILENAME,
  FORMING_YAML_FILENAME,
  SLICE_FILENAME,
  PATH_FILENAME,
  PART_PLACEMENT_FILENAME,
  SIM_REPORT_FILENAME,
  SLICE_PROXY_FILENAME,
  PATH_PROXY_FILENAME,
} from "@/lib/utils/file-naming";

const fileAttachmentArchitectSchema = z
  .object({ uploaded_by: z.string().optional(), original_name: z.string().optional() })
  .passthrough();

export function getFileUploadedBy(file: WorkspaceFileAttachment): string | undefined {
  const parsed = fileAttachmentArchitectSchema.safeParse(file.properties?.architect);
  return parsed.success ? parsed.data.uploaded_by : undefined;
}

export function getOriginalFileName(file: WorkspaceFileAttachment): string | undefined {
  const parsed = fileAttachmentArchitectSchema.safeParse(file.properties?.architect);
  return parsed.success ? parsed.data.original_name : undefined;
}

export function resolveFileParams<T>({
  fileParams,
  file,
}: {
  fileParams: Record<string, T> | undefined;
  file: WorkspaceFileAttachment | undefined;
}): T | undefined {
  if (!fileParams || !file) return undefined;
  return (
    (file.file_md5 && fileParams[file.file_md5]) || (file.id ? fileParams[file.id] : undefined)
  );
}

export function hasFileExtension(filePathOrUrl: string, extension: string): boolean {
  const normalizedExt = (extension.startsWith(".") ? "" : ".") + extension.toLowerCase();

  try {
    const urlObj = new URL(filePathOrUrl);
    const pathname = urlObj.pathname.toLowerCase();
    return pathname.endsWith(normalizedExt);
  } catch {
    // If it's not a valid URL, treat it as a filename
    return filePathOrUrl.toLowerCase().endsWith(normalizedExt);
  }
}

export function hasAnyFileExtension(filePathOrUrl: string, extensions: string[]): boolean {
  if (extensions.length === 0) {
    return true;
  }

  return extensions.some((extension) => hasFileExtension(filePathOrUrl, extension));
}

export function filterFilesByExtensions(
  files: WorkspaceFileAttachment[],
  allowedExtensions?: string[]
): WorkspaceFileAttachment[] {
  if (!allowedExtensions || allowedExtensions.length === 0) {
    return files;
  }

  return files.filter((file) => hasAnyFileExtension(file.file_name, allowedExtensions));
}

export function extractCsvFiles(
  attachedFiles: Record<string, WorkspaceFileAttachment> | undefined
): WorkspaceFileAttachment[] {
  if (!attachedFiles) return [];

  return Object.entries(attachedFiles)
    .filter(([, fileData]) => hasFileExtension(fileData.file_name, "csv"))
    .map(([fileId, fileData]) => ({
      ...fileData,
      id: fileId,
    }));
}

export function normalizeAttachedFiles(
  attachedFiles: Record<string, WorkspaceFileAttachment> | undefined
): WorkspaceFileAttachment[] {
  if (!attachedFiles) {
    return [];
  }

  return Object.entries(attachedFiles).map(([fileId, fileData]) => ({
    ...fileData,
    id: fileData.id ?? fileId,
  }));
}

// Artifacts are resolved by exact canonical filename — no extension/suffix matching.
export const ARTIFACT_ROLES = [
  "skirted-mesh",
  "slice",
  "path",
  "form-yaml",
  "part-placement",
  "sim-report",
] as const;

export type ArtifactRole = (typeof ARTIFACT_ROLES)[number];

const ARTIFACT_FILENAME: Record<ArtifactRole, string> = {
  "skirted-mesh": SKIRTED_MESH_FILENAME,
  slice: SLICE_FILENAME,
  path: PATH_FILENAME,
  "form-yaml": FORMING_YAML_FILENAME,
  "part-placement": PART_PLACEMENT_FILENAME,
  "sim-report": SIM_REPORT_FILENAME,
};

/**
 * Rendering proxies: derived media a role is drawn from. Deliberately not separate artifacts — a
 * slice without its proxy is data nothing can display, and nothing regenerates one. Anything that
 * moves, copies or clears an artifact must move its proxy with it, so both come from one table
 * rather than each caller matching `.glb` suffixes for itself (SW-1264).
 */
const ARTIFACT_PROXY_FILENAME: Partial<Record<ArtifactRole, string>> = {
  slice: SLICE_PROXY_FILENAME,
  path: PATH_PROXY_FILENAME,
};

export const artifactFileName = (role: ArtifactRole): string => ARTIFACT_FILENAME[role];
export const artifactProxyFileName = (role: ArtifactRole): string | undefined =>
  ARTIFACT_PROXY_FILENAME[role];

export const findArtifactProxy = (
  files: WorkspaceFileAttachment[],
  role: ArtifactRole
): WorkspaceFileAttachment | undefined => {
  const proxy = ARTIFACT_PROXY_FILENAME[role];
  return proxy ? files.find((f) => f.file_name === proxy) : undefined;
};

/** Every file that constitutes this role: the data, plus any proxy drawn in its place. */
export const filesForRole = (
  files: WorkspaceFileAttachment[],
  role: ArtifactRole
): WorkspaceFileAttachment[] => {
  const artifact = findArtifact(files, role);
  const proxy = findArtifactProxy(files, role);
  return [...(artifact ? [artifact] : []), ...(proxy ? [proxy] : [])];
};

export const matchesArtifactRole = (fileName: string, role: ArtifactRole): boolean =>
  fileName === ARTIFACT_FILENAME[role];

export const roleForFileName = (fileName: string): ArtifactRole | undefined =>
  (Object.keys(ARTIFACT_FILENAME) as ArtifactRole[]).find((role) =>
    matchesArtifactRole(fileName, role)
  );

export const findArtifact = (
  files: WorkspaceFileAttachment[],
  role: ArtifactRole
): WorkspaceFileAttachment | undefined =>
  files.find((f) => f.file_name === ARTIFACT_FILENAME[role]);

export const findAllArtifacts = (
  files: WorkspaceFileAttachment[],
  role: ArtifactRole
): WorkspaceFileAttachment[] => files.filter((f) => f.file_name === ARTIFACT_FILENAME[role]);

export function getCsvFilesFromPartAndProcedure(
  partAttachedFiles: Record<string, WorkspaceFileAttachment> | undefined,
  procedureAttachedFiles: Record<string, WorkspaceFileAttachment> | undefined
): WorkspaceFileAttachment[] {
  const allFiles = [
    ...normalizeAttachedFiles(partAttachedFiles),
    ...normalizeAttachedFiles(procedureAttachedFiles),
  ].filter((file) => hasFileExtension(file.file_name, "csv"));

  // Deduplicate by file ID
  const seenIds = new Set<string>();
  return allFiles.filter((file) => {
    const fileId = file.id ?? "";
    if (seenIds.has(fileId)) {
      return false;
    }
    seenIds.add(fileId);
    return true;
  });
}
